import React, { useContext } from 'react'
import { css } from 'emotion'
import { Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { NavLink } from 'react-router-dom'
import ScrollView from 'src/components/utils/ScrollView'
import { useMe, useMyNotificationCount, useOrganization } from 'src/graphql/hooks'
import ThemeContext from 'src/libraries/contexts/Theme'
import uiConstants from 'config/constants/ui'

const DrawerMenu = ({
   horizontalPlacement,
   onCloseButtonClick,
   onOverlayClick,
   visible
}) => {
   const theme = useContext(ThemeContext)
   const { organization } = useOrganization()
   const { me, loading: meLoading, isLoggedIn } = useMe()
   const { notificationCount } = useMyNotificationCount()

   const panelProps = {
      [horizontalPlacement]: '0px'
   }

   const openAnimationPosition = horizontalPlacement === 'left'
      ? 'translate3d(-100%, 0, 0)'
      : 'translate3d(100%, 0, 0)'

   // When DrawerMenu is visible, Panel becomes a ScrollView component
   // We only want this to happen when it is visible as it will disabled scrolling
   // for the rest of the contents underneath
   const DrawerMenuPanel = visible ? ScrollView : View

   return (
      <View
         className={css` pointer-events: ${visible ? 'auto' : 'none'}; `}
         position="fixed"
         top="0px"
         left="0px"
         right="0px"
         bottom="0px"
         textAlign="left"
         zIndex={uiConstants.zIndexes.drawerMenu + 9999}>
         <View
            id="globalNavDrawerControlOverlay"
            onClick={onOverlayClick}
            onKeyPress={onOverlayClick}
            role="button"
            tabIndex={0}
            position="absolute"
            top="0px"
            left="0px"
            right="0px"
            bottom="0px"
            backgroundColor="rgba(0,0,0,0.5)"
            transition="opacity 300ms"
            transform={visible ? 'translate3d(0, 0, 0)' : 'translate3d(-100%, 0, 0)'}
            opacity={visible ? '1' : '0'}
         />
         <DrawerMenuPanel
            {...panelProps}
            position="absolute"
            top="0px"
            bottom="0px"
            width="80%[a-b] 480px[c-f]"
            backgroundColor={theme.globalNavBackgroundColor === 'transparent'
               ? '#000'
               : theme.globalNavBackgroundColor
            }
            transition="300ms"
            transform={visible ? 'translate3d(0, 0, 0)' : openAnimationPosition}
            opacity={visible ? '1' : '0'}
            scroll="on">
            <View
               float="left"
               width="100%"
               height={theme.globalNavSize}
               display="flex"
               justifyContent="flex-end"
               alignItems="center"
               paddingRight={theme.viewportPaddingRight}>
               <div
                  id="globalNavDrawerControlCloseButton"
                  onClick={onCloseButtonClick}
                  onKeyPress={onCloseButtonClick}
                  role="button"
                  tabIndex={0}
                  style={{ outline: 'none' }}>
                  <Text
                     size="3"
                     color={theme.globalNavTextColor}
                     lineHeight="140%">
                     Close
                  </Text>
               </div>
            </View>
            {isLoggedIn && (
               <View
                  onClick={onCloseButtonClick}
                  float="left"
                  width="100%"
                  padding="30px">
                  <View paddingBottom="5px">
                     <NavLink to="/profile">
                        <Text
                           size="6"
                           color={theme.globalNavTextColor}
                           lineHeight="150%"
                           weight="medium">
                           {me.fullName}
                        </Text>
                     </NavLink>
                  </View>
                  <View>
                     <NavLink to="/profile">
                        <Text
                           size="5"
                           color={theme.globalNavTextColor}
                           lineHeight="170%">
                           View Profile
                        </Text>
                     </NavLink>
                     <NavLink to="/messages">
                        <View display="flex" alignItems="center" position="relative">
                           <Text
                              size="5"
                              color={theme.globalNavTextColor}
                              lineHeight="170%">
                              {/* TODO: Rename this to Messages when app is more than an inbox */}
                              Inbox
                           </Text>
                           {notificationCount && (
                              <View
                                 display="flex"
                                 justifyContent="center"
                                 alignItems="center"
                                 width="18px"
                                 height="18px"
                                 borderRadius="50%"
                                 marginLeft="9px"
                                 backgroundColor="#ff5c45">
                                 <Text size="0.85" color="#fff">
                                    {notificationCount}
                                 </Text>
                              </View>
                           )}
                        </View>
                     </NavLink>
                  </View>
               </View>
            )}
            <View
               onClick={onCloseButtonClick}
               float="left"
               width="100%"
               padding="30px"
               borderTop={`1px solid ${theme.globalNavDividerColor}`}>
               <NavLink exact to="/">
                  <Text
                     size="5"
                     color={theme.globalNavTextColor}
                     lineHeight="170%">
                     Home
                  </Text>
               </NavLink>
               {/* TODO: Fix this
               This will change when we decide how to handle Explore "Page" or "App"
               See PR #557 */}
               {organization.slug !== 'gns3' && (
                  <NavLink to="/explore">
                     <Text
                        size="5"
                        color={theme.globalNavTextColor}
                        lineHeight="170%">
                        Explore
                     </Text>
                  </NavLink>
               )}
               {organization.globalNavLinks.map((link) => {
                  if (link.__typename === 'NavExternalLinkElement') {
                     return (
                        <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer">
                           <Text
                              size="5"
                              color={theme.globalNavTextColor}
                              lineHeight="170%">
                              {link.name}
                           </Text>
                        </a>
                     )
                  }

                  return (
                     <NavLink key={link.id} to={link.url}>
                        <Text
                           size="5"
                           color={theme.globalNavTextColor}
                           lineHeight="170%">
                           {link.name}
                        </Text>
                     </NavLink>
                  )
               })}
            </View>
            <View
               onClick={onCloseButtonClick}
               float="left"
               width="100%"
               padding="30px"
               borderTop={`1px solid ${theme.globalNavDividerColor}`}>
               {isLoggedIn && (
                  <NavLink to="/account/logout">
                     <Text
                        size="5"
                        color={theme.globalNavTextColor}
                        lineHeight="170%">
                        Logout
                     </Text>
                  </NavLink>
               )}
               {!meLoading && !isLoggedIn && (
                  <NavLink to="/login">
                     <Text
                        size="5"
                        color={theme.globalNavTextColor}
                        lineHeight="170%">
                        Login
                     </Text>
                  </NavLink>
               )}
            </View>
         </DrawerMenuPanel>
      </View>
   )
}

DrawerMenu.propTypes = {
   horizontalPlacement: PropTypes.oneOf(['left', 'right']),
   onCloseButtonClick: PropTypes.func.isRequired,
   onOverlayClick: PropTypes.func.isRequired,
   visible: PropTypes.bool
}

DrawerMenu.defaultProps = {
   horizontalPlacement: 'right',
   visible: false
}

export default DrawerMenu
